import React, { useState, useEffect } from "react";
import { untranslated, useTranslation } from "@i18n";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import Box from "@mui/material/Box";
import axiosWrapper from "@Services/axiosWrapper";
import type { StatusResponse } from "@Helpers/schemas/common.schema.js";
import { getBackendUrl } from "@Utils";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR, SOMETHING_WENT_WRONG } from "@Services/constants";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import Text from "@CubeComponents/html/Text/Text";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";

interface VersionNotesProps {
  isVisible: boolean;
  onClose: () => void;
  module: "file" | "clash" | "schedule" | "estimate" | "costDatabase";
  moduleData: {
    id?: string;
    versionId?: string;
    fileVersionId?: string;
    costDatabaseId?: string;
    clashVersionId?: string;
    noteId?: string;
    projectId?: string;
    spaceId?: string;
  };
  existingNote?: string;
  /**
   * Called with the saved note body. Declared `never` — the widest contravariant position — because
   * consumers annotate it inconsistently (`() => void` in three places, `(savedNote: string) => void`
   * in VersionContent), and no single declared parameter type is assignable to all of them.
   */
  onSuccess?: (data: never) => void;
}

const VersionNotesComponent: React.FC<VersionNotesProps> = ({
  isVisible,
  onClose,
  module,
  moduleData,
  existingNote = "",
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [noteContent, setNoteContent] = useState<string>("");
  const [loadingNotes, setLoadingNotes] = useState<boolean>(false);

  useEffect(() => {
    if (isVisible) {
      setNoteContent(existingNote || "");
    }
  }, [isVisible, existingNote]);

  const handleNoteChange = (value: string) => {
    setNoteContent(value);
  };

  const getApiConfig = () => {
    const baseConfig = {
      url: "",
      // Per-module request body; each branch below replaces it wholesale.
      payload: {} as Record<string, unknown>,
      successMessage: untranslated(""),
      errorMessage: untranslated(""),
    };

    const isEditing = existingNote && existingNote.length > 0 && moduleData.noteId;

    if (module === "file") {
      baseConfig.url = `${getBackendUrl()}/fileOperations/saveNoteInFileVersion`;
      baseConfig.payload = {
        action: isEditing ? "edit" : "save",
        fileVersionId: moduleData.fileVersionId,
        note: noteContent,
      };
    } else if (module === "schedule") {
      if (isEditing) {
        baseConfig.url = `${getBackendUrl()}/scheduleVersions/updateVersionNote`;
        baseConfig.payload = {
          versionId: moduleData.versionId,
          noteId: moduleData.noteId,
          note: noteContent,
        };
      } else {
        baseConfig.url = `${getBackendUrl()}/scheduleVersions/addVersionNote`;
        baseConfig.payload = {
          versionId: moduleData.versionId,
          note: noteContent,
        };
      }
    } else if (module === "estimate") {
      baseConfig.url = `${getBackendUrl()}/estimate/updateEstimateVersionNote`;
      baseConfig.payload = {
        estimateId: moduleData.id,
        estimateVersionId: moduleData.versionId,
        versionNumber: Number(moduleData.noteId),
        note: noteContent,
      };
    } else if (module === "costDatabase") {
      baseConfig.url = `${getBackendUrl()}/costDatabase/saveCostDatabaseVersionNote`;
      baseConfig.payload = {
        id: moduleData.id,
        note: noteContent,
        costDatabaseId: moduleData.costDatabaseId,
      };
    } else if (module === "clash") {
      baseConfig.url = `${getBackendUrl()}/clashTests/saveClashVersionNote`;
      baseConfig.payload = {
        id: moduleData.clashVersionId,
        note: noteContent,
      };
    }
    baseConfig.successMessage = isEditing ? t("note_updated_successfully") : t("note_added_successfully");
    baseConfig.errorMessage = isEditing ? t("Note could not be updated") : t("Note could not be added");

    return baseConfig;
  };

  const handleSaveNote = async () => {
    if (!noteContent.trim()) {
      dispatch(
        showToastNotification({
          content: t("Note content cannot be empty"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }

    setLoadingNotes(true);
    const apiConfig = getApiConfig();

    // Dynamic URL from apiConfig - endpoint not known at compile time
    const response = await axiosWrapper.post<StatusResponse>(apiConfig.url, apiConfig.payload);

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: untranslated(apiConfig.successMessage),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      onSuccess?.(noteContent as never);
      handleClose();
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setLoadingNotes(false);
  };

  const handleClose = () => {
    setNoteContent("");
    onClose();
  };

  const isEditorEmpty = (content: string) => {
    if (!content) return true;
    const textOnly = content.replaceAll(/<[^>]*>/g, "").trim();
    return textOnly.length === 0;
  };

  const getModalHeader = () => (
    <Box className="w-full flex flex-row justify-between items-center rounded-sg">
      <Box className="text-2xl text-primary_text">{existingNote.length > 0 ? t("Edit Note") : t("Add Note")}</Box>
    </Box>
  );

  const getModalContent = () => (
    <Box className="w-full">
      <SimpleEditor
        showToolbar={true}
        showCharCount={false}
        maxLength={1000}
        value={noteContent}
        onChange={handleNoteChange}
      />
    </Box>
  );

  const getModalFooter = () => (
    <Box className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleSaveNote}
        loading={loadingNotes}
        disabled={isEditorEmpty(noteContent)}
      >
        {existingNote.length > 0 ? t("Update") : t("Confirm")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleClose} disabled={loadingNotes}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </Box>
  );

  return (
    <ModalStateDialogComponent
      headerTitle={getModalHeader()}
      modelContent={getModalContent()}
      footerContent={getModalFooter()}
      isVisible={isVisible}
      width="30%"
      onCancel={handleClose}
    />
  );
};

export default VersionNotesComponent;
